import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FaWhatsapp } from "react-icons/fa";
// ✅ Fixed import path - 4 levels up
import { dangerMessage } from "../../../../components/Toast/Toast";

const WhatsAppForm = ({ setIsToggleViewWhatsApp, attendeeID, eventID, token, phoneNumber, attendeeData }) => {
  const [message, setMessage] = useState("");
  const [btnLoad, setBtnLoad] = useState(false);
  const [formattedPhone, setFormattedPhone] = useState("");
  const queryClient = useQueryClient();
  const key = ["allWhatsApp", eventID];

  // Format phone number when component loads
  useEffect(() => {
    if (phoneNumber || attendeeData?.phoneNumber) {
      const phone = phoneNumber || attendeeData?.phoneNumber;
      // Remove all non-digit characters
      const cleaned = phone.replace(/[\s\-\(\)\.]/g, "");
      
      // Add South African country code if missing
      if (cleaned.startsWith("0")) {
        // Remove the leading 0 and add +27
        setFormattedPhone("+27" + cleaned.substring(1));
      } else if (cleaned.startsWith("27")) {
        // Already has 27, just add +
        setFormattedPhone("+" + cleaned);
      } else if (cleaned.startsWith("+")) {
        // Already has +
        setFormattedPhone(cleaned);
      } else {
        // Assume it's a local number without country code
        setFormattedPhone("+27" + cleaned);
      }
    }
  }, [phoneNumber, attendeeData]);

  const handleSend = async (e) => {
    e.preventDefault();
    
    const phoneToUse = formattedPhone;
    
    if (!phoneToUse) {
      dangerMessage("No phone number found for this attendee");
      return;
    }

    // Validate phone number format
    const phoneRegex = /^\+[1-9]\d{6,14}$/;
    if (!phoneRegex.test(phoneToUse)) {
      dangerMessage("Invalid phone number format. Please ensure the attendee has a valid phone number.");
      return;
    }

    setBtnLoad(true);
    
    try {
      // Create WhatsApp URL
      const whatsappUrl = `https://wa.me/${phoneToUse}?text=${encodeURIComponent(message)}`;
      
      // Open WhatsApp in new window
      const whatsappWindow = window.open(whatsappUrl, "_blank");
      
      if (!whatsappWindow || whatsappWindow.closed || typeof whatsappWindow.closed === 'undefined') {
        // Popup was blocked
        dangerMessage("WhatsApp was blocked by your browser. Please allow popups for this site.");
        setBtnLoad(false);
        return;
      }
      
      // Close modal after opening WhatsApp
      setTimeout(() => {
        setIsToggleViewWhatsApp(false);
        setBtnLoad(false);
      }, 1000);
      
    } catch (error) {
      console.error("WhatsApp error:", error);
      dangerMessage("Failed to open WhatsApp. Please try again.");
      setBtnLoad(false);
    }
  };

  // Get display phone number
  const displayPhone = phoneNumber || attendeeData?.phoneNumber || "this attendee";

  return (
    <div style={styles.formContainer}>
      <div style={styles.header}>
        <div style={styles.iconWrapper}>
          <FaWhatsapp style={styles.icon} />
        </div>
        <div>
          <h4 style={styles.title}>Send WhatsApp</h4>
          <p style={styles.subtitle}>
            Send a WhatsApp message to {displayPhone}
          </p>
          {formattedPhone && (
            <p style={{ ...styles.subtitle, marginTop: "4px", fontSize: "12px" }}>
              Using: {formattedPhone}
            </p>
          )}
        </div>
      </div>

      <form onSubmit={handleSend}>
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Message<span className="text-danger">*</span>
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={styles.textarea}
            placeholder="Type your WhatsApp message here..."
            rows={6}
            required
          />
          <small style={styles.hint}>
            This will open WhatsApp Web/Desktop with your message pre-filled
          </small>
        </div>

        <div style={styles.buttonContainer}>
          <button
            type="button"
            onClick={() => setIsToggleViewWhatsApp(false)}
            style={styles.cancelButton}
            disabled={btnLoad}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{
              ...styles.submitButton,
              opacity: btnLoad ? 0.7 : 1,
              cursor: btnLoad ? "not-allowed" : "pointer",
            }}
            disabled={btnLoad || !formattedPhone}
          >
            {btnLoad ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" style={{ width: "14px", height: "14px" }} />
                Opening...
              </>
            ) : (
              <>
                <FaWhatsapp style={{ marginRight: "8px" }} />
                Send via WhatsApp
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  formContainer: { padding: "8px" },
  header: {
    display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px",
    padding: "16px", background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
    borderRadius: "12px", border: "1px solid #86efac",
  },
  iconWrapper: {
    width: "56px", height: "56px", borderRadius: "12px",
    background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)",
  },
  icon: { color: "white", fontSize: "28px" },
  title: { margin: "0 0 4px 0", fontSize: "18px", fontWeight: "600", color: "#111827" },
  subtitle: { margin: 0, fontSize: "13px", color: "#6b7280" },
  formGroup: { marginBottom: "20px" },
  label: { display: "block", fontSize: "14px", fontWeight: "600", color: "#374151", marginBottom: "8px" },
  textarea: {
    width: "100%", padding: "12px 16px", border: "2px solid #e5e7eb", borderRadius: "10px",
    fontSize: "14px", resize: "vertical", fontFamily: "inherit", transition: "all 0.2s", boxSizing: "border-box",
  },
  hint: { display: "block", marginTop: "8px", fontSize: "12px", color: "#6b7280", fontStyle: "italic" },
  buttonContainer: {
    display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "24px",
    paddingTop: "20px", borderTop: "2px solid #f3f4f6",
  },
  cancelButton: {
    padding: "10px 20px", border: "2px solid #e5e7eb", background: "white", color: "#374151",
    borderRadius: "10px", fontSize: "14px", fontWeight: "600", cursor: "pointer", transition: "all 0.2s",
  },
  submitButton: {
    padding: "10px 24px", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "600",
    display: "flex", alignItems: "center", transition: "all 0.2s",
    background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)", color: "white",
    boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)",
  },
};

export default WhatsAppForm;